import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Ban, CheckCircle, CalendarPlus, Save, Trash2, Plus, BookOpen, Gift } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

import { confirmar } from "@/components/ConfirmDialog";
const pedirTaxa = () => {
  const t = window.prompt("Taxa que a professora (ou o Representante Credenciado da turma) paga à escola por este bônus (R$).\nDeixe em branco se não houver.", "");
  if (t === null) return undefined;           // cancelou
  const n = parseFloat(String(t).replace(/\./g, "").replace(",", "."));
  return isNaN(n) ? null : n;
};

export const STATE_META = {
  ativa: { label: "Ativa", cls: "bg-green-50 text-green-700 ring-1 ring-green-200" },
  bloqueada: { label: "Bloqueada", cls: "bg-red-50 text-red-700 ring-1 ring-red-200" },
  expirada: { label: "Expirada", cls: "bg-amber-50 text-amber-800 ring-1 ring-amber-200" },
  agendada: { label: "Agendada", cls: "bg-sky-50 text-sky-700 ring-1 ring-sky-200" },
  "sem acesso": { label: "Sem acesso", cls: "bg-slate-100 text-slate-500" },
};

export const fmtDate = (d) => (d ? d.split("-").reverse().join("/") : "");

/** Matrículas de um aluno: datas de início/validade, prolongar, bloquear, cancelar e matricular. */
export const EnrollmentsDialog = ({ student, onClose, onChanged }) => {
  const [items, setItems] = useState(null);
  const [courses, setCourses] = useState([]);
  const [drafts, setDrafts] = useState({}); // course_id -> { starts_at, expires_at }
  const [busy, setBusy] = useState("");
  const [addId, setAddId] = useState("");
  const [addBonus, setAddBonus] = useState(false);
  const { user: authUser } = useAuth();
  const isAdmin = authUser?.role === "admin";

  const apply = (list) => {
    setItems(list);
    setDrafts(Object.fromEntries(list.map((i) => [i.course_id, { starts_at: i.starts_at, expires_at: i.expires_at }])));
  };

  useEffect(() => {
    if (!student) return;
    setItems(null);
    setAddId("");
    api.get(`/students/${student.id}/enrollments`).then((r) => apply(r.data.items)).catch(() => apply([]));
    api.get("/courses").then((r) => setCourses(r.data)).catch(() => setCourses([]));
  }, [student]);

  const run = async (key, fn, ok) => {
    setBusy(key);
    try {
      const r = await fn();
      apply(r.data.items);
      if (ok) toast.success(ok);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const save = (it, patch = {}, ok = "Matrícula atualizada.") => {
    const d = drafts[it.course_id] || {};
    return run(`save-${it.course_id}`, () => api.put(`/students/${student.id}/enrollments/${it.course_id}`, {
      status: it.status, starts_at: d.starts_at || "", expires_at: d.expires_at || "", ...patch,
    }), ok);
  };
  const vitalicio = async (it) => {
    if (!(await confirmar(`Tornar o acesso de ${student.name} em "${it.title}" VITALÍCIO (sem data de validade)?`))) return;
    save(it, { starts_at: it.starts_at || "", expires_at: "" }, "Acesso vitalício liberado.");
  };
  const extend = (it, days) => run(`ext-${it.course_id}`, () => api.post(`/students/${student.id}/enrollments/${it.course_id}/extend`, { days }), `Validade prolongada em ${days} dias.`);
  const cancel = async (it) => {
    const msg = it.groups.length
      ? `Cancelar a matrícula direta e as datas de "${it.title}"? O acesso pela turma (${it.groups.join(", ")}) continua.`
      : `Cancelar a matrícula de ${student.name} em "${it.title}"?`;
    if (!(await confirmar(msg))) return;
    run(`del-${it.course_id}`, () => api.delete(`/students/${student.id}/enrollments/${it.course_id}`), "Matrícula cancelada.");
  };
  const enroll = () => {
    if (!addId) return;
    const taxa = addBonus && isAdmin ? pedirTaxa() : null;
    if (taxa === undefined) return;
    run("add", () => (addBonus
      ? api.post(`/students/${student.id}/enrollments/${addId}/bonus`, { fee: taxa })
      : api.put(`/students/${student.id}/enrollments/${addId}`, { status: "ativa", starts_at: "", expires_at: "", direct: true })),
    addBonus ? "Aluno matriculado com bônus (prazo do curso)." : "Aluno matriculado.");
    setAddId(""); setAddBonus(false);
  };
  const applyBonus = async (it) => {
    if (!(await confirmar(`Marcar "${it.title}" como bônus curso presencial? O acesso passa a valer de hoje até o fim do prazo do curso (${it.course_access_months} meses).`))) return;
    const taxa = isAdmin ? pedirTaxa() : null;
    if (taxa === undefined) return;
    run(`bonus-${it.course_id}`, () => api.post(`/students/${student.id}/enrollments/${it.course_id}/bonus`, { fee: taxa }), "Prazo do curso aplicado.");
  };

  const setDraft = (cid, k, v) => setDrafts((p) => ({ ...p, [cid]: { ...p[cid], [k]: v } }));
  const available = courses.filter((c) => !(items || []).some((i) => i.course_id === c.id && i.enrolled));

  return (
    <Dialog open={!!student} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto iep-scroll">
        <DialogHeader>
          <DialogTitle className="font-display">Matrículas — {student?.name}</DialogTitle>
          <DialogDescription>Defina início e validade, prolongue, bloqueie ou cancele o acesso de cada curso.</DialogDescription>
        </DialogHeader>

        {items === null ? (
          <div className="py-10 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : (
          <div className="space-y-3">
            {items.length === 0 && <p className="text-sm text-slate-400 py-4">Nenhuma matrícula ainda.</p>}
            {items.map((it) => {
              const meta = STATE_META[it.enrolled ? it.state : "sem acesso"];
              const d = drafts[it.course_id] || {};
              const dirty = (d.starts_at || "") !== (it.starts_at || "") || (d.expires_at || "") !== (it.expires_at || "");
              return (
                <div key={it.course_id} className="rounded-xl border border-slate-200 p-4" data-testid={`enrollment-${it.course_id}`}>
                  <div className="flex items-start gap-3 flex-wrap">
                    <BookOpen className="h-5 w-5 text-[#1E9E2A] mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-800">{it.title}</div>
                      <div className="flex flex-wrap gap-1.5 mt-1">
                        {it.bonus && <span className="text-[11px] px-2 py-0.5 rounded bg-green-100 text-green-800 font-semibold inline-flex items-center gap-1"><Gift className="h-3 w-3" /> Bônus curso presencial</span>}
                        {it.gate_note && <span className="text-[11px] px-2 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold">{it.gate_note}</span>}
                        {it.completed && <span className="text-[11px] px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 font-semibold">Concluído{it.completed_at ? ` em ${fmtDate(it.completed_at)}` : ""}</span>}
                        {it.direct && <span className="text-[11px] px-2 py-0.5 rounded bg-slate-100 text-slate-600">Matrícula direta</span>}
                        {it.groups.map((g) => <span key={g} className="text-[11px] px-2 py-0.5 rounded bg-sky-50 text-sky-700">Turma: {g}</span>)}
                      </div>
                    </div>
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${meta.cls}`}>{meta.label}</span>
                  </div>

                  <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-3 mt-3 items-end">
                    <div className="space-y-1">
                      <label className="text-xs text-slate-500">Início do acesso</label>
                      <Input type="date" value={d.starts_at || ""} onChange={(e) => setDraft(it.course_id, "starts_at", e.target.value)} />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs text-slate-500">Válido até (vazio = sem prazo)</label>
                      <Input type="date" value={d.expires_at || ""} onChange={(e) => setDraft(it.course_id, "expires_at", e.target.value)} data-testid={`expires-${it.course_id}`} />
                    </div>
                    <Button onClick={() => save(it)} disabled={!dirty || !!busy} className="text-white" style={{ background: "#232D4F" }} data-testid={`save-enrollment-${it.course_id}`}>
                      {busy === `save-${it.course_id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" /> Salvar datas</>}
                    </Button>
                  </div>

                  <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-slate-100">
                    <span className="text-xs text-slate-500 mr-1 flex items-center gap-1"><CalendarPlus className="h-3.5 w-3.5" /> Prolongar:</span>
                    {[30, 90, 180, 365].map((n) => (
                      <Button key={n} size="sm" variant="outline" disabled={!!busy} onClick={() => extend(it, n)} data-testid={`extend-${n}-${it.course_id}`}>+{n} dias</Button>
                    ))}
                    {isAdmin && (it.enrolled && !it.expires_at
                      ? <span className="text-xs font-semibold text-[#1E9E2A] px-2">♾ Vitalício</span>
                      : <Button size="sm" variant="outline" className="text-[#926B38] border-[#E3C48F]" disabled={!!busy} onClick={() => vitalicio(it)} data-testid={`lifetime-${it.course_id}`}>♾ Vitalício</Button>)}
                    <div className="flex-1" />
                    {!it.bonus && it.course_access_months > 0 && (
                      <Button size="sm" variant="outline" className="text-green-700" disabled={!!busy} onClick={() => applyBonus(it)} title="Aplicar o prazo definido no curso">
                        <Gift className="h-4 w-4 mr-1" /> Bônus ({it.course_access_months} meses)
                      </Button>
                    )}
                    {it.status === "bloqueada" ? (
                      <Button size="sm" variant="outline" className="text-green-700" disabled={!!busy} onClick={() => save(it, { status: "ativa" }, "Matrícula liberada.")}>
                        <CheckCircle className="h-4 w-4 mr-1" /> Liberar
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" className="text-red-600" disabled={!!busy} onClick={() => save(it, { status: "bloqueada" }, "Matrícula bloqueada.")} data-testid={`block-${it.course_id}`}>
                        <Ban className="h-4 w-4 mr-1" /> Bloquear
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" className="text-slate-500 hover:text-red-600" disabled={!!busy} onClick={() => cancel(it)} title="Cancelar matrícula">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              );
            })}

            <div className="rounded-xl border-2 border-dashed border-slate-200 p-4 flex flex-wrap gap-2 items-center">
              <span className="text-sm font-medium text-slate-700 mr-1">Matricular em:</span>
              <select value={addId} onChange={(e) => setAddId(e.target.value)} className="flex-1 min-w-[200px] h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="enroll-course-select">
                <option value="">Selecione um curso…</option>
                {available.map((c) => <option key={c.id} value={c.id}>{c.title}{c.published ? "" : " (rascunho)"}</option>)}
              </select>
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer w-full sm:w-auto order-last sm:order-none">
                <Checkbox checked={addBonus} onCheckedChange={(v) => setAddBonus(!!v)} /> Bônus curso presencial (prazo do curso)
              </label>
              <Button onClick={enroll} disabled={!addId || !!busy} className="text-white" style={{ background: "#1E9E2A" }} data-testid="enroll-add">
                {busy === "add" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-1" /> Matricular</>}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
